import React, { useEffect, useMemo, useState } from "react"

import {
    OneVerifyIntegrationStatus,
    OneVerifyIntegrationSupportRequestType,
} from "@common-shared/types/integrations.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { MarkTileElement } from "../../elements/markTile.element"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { integrationsSubmitSupportRequest } from "../../store/thunks/integrations.thunks"
import { type IOneVerifyIntegration } from "../../types/integrations.types"

export const IntegrationDetailComponent: React.FunctionComponent<
    IIntegrationDetailComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [submitError, setSubmitError] = useState<string | null>(null)
    const [didSubmitInterest, setDidSubmitInterest] = useState<boolean>(false)

    const isNotifySubmitting = useMemo<boolean>(() => {
        return RuntimeHelper.isIntegrationSupportRequestLoading()
    }, [runtimeState.isLoading])

    const isComingSoon = useMemo<boolean>(() => {
        return (
            !!props.integration &&
            props.integration.status === OneVerifyIntegrationStatus.comingSoon
        )
    }, [props.integration])

    const isLive = useMemo<boolean>(() => {
        return (
            !!props.integration &&
            props.integration.status === OneVerifyIntegrationStatus.live
        )
    }, [props.integration])

    const shouldShowConnectCta = useMemo<boolean>(() => {
        return !!props.integration && !props.integration.hideConnectCta
    }, [props.integration])

    useEffect(() => {
        setDidSubmitInterest(false)
        setSubmitError(null)
    }, [props.integration?.id])

    const onConnectClick = () => {
        setSubmitError(null)

        if (!selectedPracticeRole) {
            setSubmitError(
                "Practice context is not available. Refresh the page and try again.",
            )

            return
        }

        if (!props.integration) {
            setSubmitError("Select an integration above, then try again.")

            return
        }

        dispatch(
            integrationsSubmitSupportRequest({
                practice: selectedPracticeRole.practice,
                payload: {
                    requestType: isComingSoon
                        ? OneVerifyIntegrationSupportRequestType.notifyMe
                        : OneVerifyIntegrationSupportRequestType.setup,
                    integrationId: props.integration.id,
                    integrationName: props.integration.name,
                    integrationCategory: props.integration.category,
                    integrationStatus: props.integration.status,
                },
                onSuccess: () => {
                    setDidSubmitInterest(true)
                },
            }),
        )
    }

    if (!props.integration) {
        return (
            <div
                data-produktly="integrations-detail"
                className={`
                    rounded-[8px] p-[24px] border border-solid border-brand-warmLight2
                    bg-brand-white
                    ${props.className ?? ""}
                `}
            >
                <PrimaryText color={NIRVANA_COLORS.brand.grey7}>
                    Select an integration to see details.
                </PrimaryText>
            </div>
        )
    }

    return (
        <div
            data-produktly="integrations-detail"
            className={`
                rounded-[8px] p-[24px] md:p-[32px]
                border border-solid border-brand-warmLight2 bg-brand-white
                ${props.className ?? ""}
            `}
        >
            <div className="flex flex-col gap-[16px]">
                <div className="flex gap-[16px] items-start">
                    <MarkTileElement
                        icon={props.integration.markIcon}
                        backgroundColor={props.integration.markBackgroundColor}
                        markImageUrl={props.integration.markImageUrl}
                        markImageFit={props.integration.markImageFit}
                        tileLabel={props.integration.name}
                        size="large"
                    />

                    <div>
                        <PrimaryText typography="h4" weight="semibold">
                            {props.integration.name}
                        </PrimaryText>

                        <PrimaryText
                            className="mt-[8px] block max-w-[640px]"
                            weight="medium"
                            color={NIRVANA_COLORS.brand.grey7}
                        >
                            {props.integration.tagline}
                        </PrimaryText>
                    </div>
                </div>

                {isComingSoon ? (
                    <div
                        className="
                            self-start rounded-full px-[14px] py-[6px] bg-brand-lilacLight
                            border border-solid border-brand-warmLight2
                        "
                    >
                        <PrimaryText size={13} lineHeight={16} weight="medium">
                            Coming soon
                        </PrimaryText>
                    </div>
                ) : null}

                {isLive ? (
                    <div
                        className="
                            self-start flex items-center gap-[8px] rounded-full px-[14px] py-[6px]
                            bg-brand-greenLight border border-solid border-brand-warmLight2
                        "
                    >
                        <span
                            aria-hidden
                            className="shrink-0 w-[6px] h-[6px] rounded-full bg-brand-greenDark"
                        />
                        <PrimaryText
                            size={13}
                            lineHeight={16}
                            weight="medium"
                            color={NIRVANA_COLORS.brand.greenDark}
                        >
                            Live
                        </PrimaryText>
                    </div>
                ) : null}

                {shouldShowConnectCta ? (
                    didSubmitInterest ? (
                        <PrimaryText
                            weight="medium"
                            color={NIRVANA_COLORS.brand.greenDark}
                        >
                            {isComingSoon
                                ? `Thanks — we will reach out when ${props.integration.name} is available.`
                                : "Thanks — we received your request and will follow up."}
                        </PrimaryText>
                    ) : (
                        <ButtonElement
                            className="w-full"
                            buttonClassName="w-full"
                            type="primary"
                            label={
                                isComingSoon
                                    ? "Ready to connect? Notify me when live"
                                    : "Ready to connect? Reach out to your CSM to get setup"
                            }
                            htmlType="button"
                            isLoading={isNotifySubmitting}
                            disabled={isNotifySubmitting}
                            onClick={onConnectClick}
                        />
                    )
                ) : null}

                {shouldShowConnectCta && submitError ? (
                    <PrimaryText size={13} color={NIRVANA_COLORS.brand.error}>
                        {submitError}
                    </PrimaryText>
                ) : null}
            </div>

            <div className="my-[24px] h-[1px] bg-brand-warmLight2" />

            {typeof props.integration.description === "string" ? (
                <PrimaryText
                    className="max-w-[720px] mb-[24px]"
                    weight="medium"
                >
                    {props.integration.description}
                </PrimaryText>
            ) : (
                props.integration.description
            )}

            {props.integration.features?.length ? (
                <ul
                    className="
                        grid grid-flow-col grid-cols-2 gap-x-[16px] gap-y-[12px] md:gap-x-[32px]
                        list-none p-0 m-0
                        sm:grid-cols-1 sm:grid-flow-row sm:[grid-template-rows:none]
                    "
                    style={{
                        gridTemplateRows: `repeat(${Math.ceil(props.integration.features.length / 2)}, minmax(0, auto))`,
                    }}
                >
                    {props.integration.features.map(feature => {
                        return (
                            <li
                                key={feature}
                                className="min-w-0 flex gap-[10px] items-start"
                            >
                                <span
                                    aria-hidden
                                    className="
                                        mt-[8px] shrink-0 w-[6px] h-[6px] rounded-full bg-brand-blue
                                    "
                                />
                                <PrimaryText
                                    className="min-w-0"
                                    weight="medium"
                                >
                                    {feature}
                                </PrimaryText>
                            </li>
                        )
                    })}
                </ul>
            ) : null}
        </div>
    )
}

interface IIntegrationDetailComponentProps {
    className?: string
    integration: IOneVerifyIntegration | null
}
